import { useRouter } from "@tanstack/react-router";
import { useRef, useState } from "react";
import {
  useContractTemplateList,
  useDeleteContractTemplate,
  useChangeContractTemplateStatus,
} from "~/services/business/contractTemplate";
import type {
  CdContractStatus,
  ContractTemplateListItem,
} from "~/types/common";
import { PinVerifyModal } from "./PinVerifyModal";
import { useSealList } from "~/services/business/seal";

/** 탭 2: 계약서 템플릿 목록·관리 — API 연동 */

const ACTIVATION_BLOCK_MESSAGE = "계약서 작성을 완료해야 활성화 할 수 있어요.";

const activationBlockReason = (tpl: ContractTemplateListItem): string => {
  // 렌더(발송용 스냅샷)는 활성화 시점에 BE 가 온디맨드로 생성하므로
  // 여기서는 작성 완료 여부(상태·검토 단계·필드 존재)만 본다.
  if (tpl.cdContractStatus === "CONTRACT_STATUS_DRAFT")
    return ACTIVATION_BLOCK_MESSAGE;
  if (tpl.cdEditStep !== "CONTRACT_EDIT_REVIEW")
    return ACTIVATION_BLOCK_MESSAGE;
  if ((tpl.fieldCount ?? 0) <= 0) return ACTIVATION_BLOCK_MESSAGE;
  return "";
};

// 배지는 저장된 상태 그대로 — 작성완료(INACTIVE)면 "비활성"으로 표시
const displayStatus = (tpl: ContractTemplateListItem): CdContractStatus =>
  tpl.cdContractStatus === "CONTRACT_STATUS_DRAFT"
    ? "CONTRACT_STATUS_DRAFT"
    : tpl.cdContractStatus;

export const ContractTabTemplates = () => {
  const { history } = useRouter();
  const [showAddModal, setShowAddModal] = useState(false);
  const { data: templates = [], isLoading } = useContractTemplateList();
  const deleteMutation = useDeleteContractTemplate();
  const statusMutation = useChangeContractTemplateStatus();
  // 도장 계약서 활성화 가드용 — 활성 도장 목록
  const { data: sealList = [] } = useSealList();
  const activeSeals = sealList.filter(
    (s) => s.activeYn === "Y" && !!s.imageUrl,
  );
  // 상태 변경 진행 여부를 동기적으로 잠그는 가드 — React 의 isPending 은 비동기라
  // 빠른 연타(더블탭)를 못 막는다. ref 로 클릭 즉시 잠가 중복 PATCH 발사를 원천 차단.
  const statusChangingRef = useRef(false);
  // 도장 필드가 있는 계약서 활성화/비활성화 시 PIN 인증 대상(모달 오픈 트리거).
  // 활성/비활성 어느 방향이든 도장 사용 승인이 필요하므로 목표 상태(next)를 함께 담는다.
  const [pinTarget, setPinTarget] = useState<{
    tpl: ContractTemplateListItem;
    next: CdContractStatus;
  } | null>(null);
  // 삭제 확인 — native confirm() 은 웹뷰/자동화 환경에서 막혀 삭제가 안 되므로 커스텀 모달로 처리.
  const [deleteTarget, setDeleteTarget] =
    useState<ContractTemplateListItem | null>(null);

  const goEdit = (seq: number | "new") => {
    history.push(`/business/layout/contract-template-edit?seq=${seq}` as any);
  };

  // 상태 변경 실행 — 활성화 시 도장 PIN(pin) 을 함께 넘길 수 있다.
  const runStatusMutate = async (
    contractSeq: number,
    next: CdContractStatus,
    pin?: string,
    opts?: { silentPinFail?: boolean },
  ): Promise<boolean> => {
    statusChangingRef.current = true;
    try {
      await statusMutation.mutateAsync({ contractSeq, status: next, pin });
      return true;
    } catch (err) {
      // 도장 PIN 오류는 PinVerifyModal 이 열린 채 실패횟수를 갱신하므로 여기선 alert 억제
      if (!opts?.silentPinFail) {
        alert(`상태 변경 실패\n${err instanceof Error ? err.message : ""}`);
      }
      return false;
    } finally {
      statusChangingRef.current = false;
    }
  };

  // 활성 위탁계약서가 곧 첫 예약 기본 — 별도 뱃지 없이 "위탁계약서" 유형 뱃지로 식별

  return (
    <div>
      {/* 상단 액션바 */}
      <div className="flex justify-between items-center px-4 py-3">
        <div className="text-[14px] font-semibold">
          총 {templates.length}개 템플릿
        </div>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="bg-[#FF9934] text-white text-[13px] font-semibold rounded-full px-4 py-1.5"
        >
          + 템플릿 추가
        </button>
      </div>

      {/* 템플릿 목록 */}
      <div className="mx-4 space-y-2">
        {isLoading && (
          <div className="text-center text-[#999] text-[13px] py-12">
            불러오는 중...
          </div>
        )}
        {!isLoading && templates.length === 0 && (
          <div className="text-center text-[#999] text-[13px] py-12">
            등록된 양식이 없어요. "+ 템플릿 추가" 로 시작해 보세요
          </div>
        )}
        {templates.map((tpl) => {
          const isActive = tpl.cdContractStatus === "CONTRACT_STATUS_ACTIVE";
          const blockReason = isActive ? "" : activationBlockReason(tpl);
          // 상태 변경 요청이 진행 중이면(특히 이 카드 대상) 스위치를 잠가 중복 발사 차단
          const statusPending =
            statusMutation.isPending &&
            statusMutation.variables?.contractSeq === tpl.contractSeq;
          const switchDisabled = !!blockReason || statusPending;
          const switchColorClass = isActive ? "bg-[#22A55B]" : "bg-[#E9E9E9]";

          return (
            <button
              type="button"
              key={tpl.contractSeq}
              onClick={() => goEdit(tpl.contractSeq)}
              className="w-full bg-white rounded-lg border border-[#e9e9e9] p-3 text-left active:bg-[#FAFAFA]"
            >
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge status={displayStatus(tpl)} />
                  <div className="text-[15px] font-semibold text-[#111]">
                    {tpl.contractName}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* 활성/비활성 스위치 */}
                  <span
                    role="switch"
                    aria-checked={isActive}
                    aria-disabled={switchDisabled}
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      // 이미 상태 변경 요청이 진행 중이면 조용히 무시 — 중복 발사 방지.
                      // (활성화 시 백엔드가 발송용 렌더를 온디맨드로 만드느라 1~2초 걸리는데,
                      //  그 사이 재클릭되면 버전 INSERT 가 충돌해 "상태 변경 실패" 팝업이 뜨던 문제)
                      // ref 로 동기 잠금 — isPending(비동기) 은 빠른 연타를 못 막으므로 병행.
                      if (statusChangingRef.current || statusPending) return;
                      if (blockReason) {
                        alert(blockReason);
                        return;
                      }
                      const next = isActive
                        ? "CONTRACT_STATUS_INACTIVE"
                        : "CONTRACT_STATUS_ACTIVE";
                      // 사업자 도장 필드가 있는 계약서는 활성화/비활성화 모두 도장 PIN 인증을 요구한다.
                      //  - 활성화 시: 등록된 활성 도장이 없으면 → 도장 등록으로 유도(활성화 불가)
                      //  - 활성화/비활성화 공통: 도장 PIN 인증 후 상태 변경
                      // 도장 필드가 없으면(서명만) 그냥 상태 변경.
                      if (tpl.hasSealField) {
                        if (
                          next === "CONTRACT_STATUS_ACTIVE" &&
                          activeSeals.length === 0
                        ) {
                          if (
                            confirm(
                              "이 계약서에는 사업장 도장이 필요해요.\n도장을 먼저 등록해야 활성화할 수 있어요.\n도장 관리로 이동할까요?",
                            )
                          ) {
                            history.push(
                              "/business/layout/seal-management" as any,
                            );
                          }
                          return;
                        }
                        setPinTarget({ tpl, next });
                        return;
                      }
                      runStatusMutate(tpl.contractSeq, next);
                    }}
                    title={
                      switchDisabled
                        ? blockReason
                        : isActive
                          ? "비활성으로 전환"
                          : "활성으로 전환"
                    }
                    className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
                      switchDisabled
                        ? "cursor-not-allowed opacity-45"
                        : "cursor-pointer"
                    } ${switchColorClass}`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${
                        isActive ? "left-[18px]" : "left-0.5"
                      }`}
                    />
                  </span>
                  {/* 삭제 */}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(tpl);
                    }}
                    className="text-[#BDBDBD] text-[18px] px-1 hover:text-[#F24747] cursor-pointer select-none"
                  >
                    🗑
                  </span>
                </div>
              </div>
              <div className="text-[12px] text-[#999999]">
                필드 {tpl.fieldCount}개 · 체결 {tpl.signedCount}건
              </div>
              <div className="flex justify-between items-end mt-0.5 gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] text-[#BDBDBD]">
                    마지막 수정 {tpl.dtUpdate?.slice(0, 10)}
                  </div>
                </div>
                {/* 기본 설정(첫 이용 시 자동 발송) 양식만 우측 하단에 뱃지 표시 */}
                {tpl.autoFirstReservYn === "Y" && (
                  <span
                    title="첫 이용 시 보호자에게 자동으로 발송되는 기본 양식"
                    className="shrink-0 inline-block text-[10px] font-bold rounded-full px-2 py-0.5 bg-[#FFF7E6] text-[#FF9934] border border-[#FFE3B3]"
                  >
                    ⚡ 기본 설정
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* 추가 모달 */}
      {showAddModal && (
        <AddTemplateModal
          onClose={() => setShowAddModal(false)}
          onCreateBlank={() => {
            setShowAddModal(false);
            goEdit("new");
          }}
          onImport={() => {
            setShowAddModal(false);
            history.push("/business/layout/contract-template-import" as any);
          }}
        />
      )}

      {/* 도장 필드 계약서 활성화/비활성화 — 도장 PIN 인증 */}
      {pinTarget && (
        <PinVerifyModal
          onClose={() => setPinTarget(null)}
          onSubmit={async (pin) => {
            const ok = await runStatusMutate(
              pinTarget.tpl.contractSeq,
              pinTarget.next,
              pin,
              { silentPinFail: true },
            );
            if (ok) setPinTarget(null);
            return ok;
          }}
        />
      )}

      {/* 삭제 확인 — 커스텀 모달(native confirm 대체) */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="w-full max-w-[360px] rounded-2xl bg-white p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[15px] font-bold text-[#111]">양식 삭제</div>
            <div className="mt-2 text-[13px] leading-relaxed text-[#555]">
              “{deleteTarget.contractName}” 양식을 삭제할까요?
              <br />이 작업은 되돌릴 수 없어요.
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="flex-1 rounded-xl border border-[#E9E9E9] py-2.5 text-[13px] font-semibold text-[#888]"
              >
                취소
              </button>
              <button
                type="button"
                disabled={deleteMutation.isPending}
                onClick={() =>
                  deleteMutation.mutate(deleteTarget.contractSeq, {
                    onSuccess: () => setDeleteTarget(null),
                  })
                }
                className="flex-[1.4] rounded-xl bg-[#F24747] py-2.5 text-[13px] font-bold text-white disabled:opacity-50"
              >
                {deleteMutation.isPending ? "삭제 중…" : "삭제"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }: { status: CdContractStatus }) => {
  const isActive = status === "CONTRACT_STATUS_ACTIVE";
  const isDraft = status === "CONTRACT_STATUS_DRAFT";
  return (
    <span
      className={`inline-block text-[10px] font-bold rounded px-1.5 py-0.5 ${
        isActive
          ? "bg-[#E6F7E9] text-[#22A55B]"
          : isDraft
            ? "bg-[#FFECEC] text-[#F24747]"
            : "bg-[#F5F5F5] text-[#999999]"
      }`}
    >
      {isActive ? "활성" : isDraft ? "작성중" : "비활성"}
    </span>
  );
};

// (TypeBadge / TYPE_STYLE / CONTRACT_TYPE_LABEL — 추후 템플릿 카드 타입 표기 도입 시 재추가)

interface AddTemplateModalProps {
  onClose: () => void;
  onCreateBlank: () => void;
  onImport: () => void;
}

const AddTemplateModal = ({
  onClose,
  onCreateBlank,
  onImport,
}: AddTemplateModalProps) => (
  <div
    className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end md:items-center justify-center"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-md p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-[18px] font-bold mb-1">새 계약서 템플릿</div>
      <div className="text-[13px] text-[#666] mb-5">어떻게 시작할까요?</div>

      <div className="space-y-3">
        <ChoiceCard
          icon="✏️"
          title="직접 작성"
          desc="빈 템플릿에서 시작"
          onClick={onCreateBlank}
        />
        <ChoiceCard
          icon="✨"
          title="AI 자동 변환"
          desc="PDF·DOCX를 AI가 분석해 변수·필드까지 자동 배치합니다. (1크레딧)"
          onClick={onImport}
        />
      </div>

      <button
        type="button"
        onClick={onClose}
        className="w-full mt-5 py-2.5 text-[13px] text-[#666] border border-[#E9E9E9] rounded-full"
      >
        취소
      </button>
    </div>
  </div>
);

const ChoiceCard = ({
  icon,
  title,
  desc,
  onClick,
  disabled,
}: {
  icon: string;
  title: string;
  desc: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full text-left bg-[#FAFAFA] hover:bg-[#FFF7E6] border border-[#E9E9E9] rounded-xl p-4 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
  >
    <div className="text-[24px]">{icon}</div>
    <div className="flex-1">
      <div className="text-[14px] font-semibold text-[#111] flex items-center gap-1.5">
        {title}
        {disabled && (
          <span className="text-[9px] bg-[#E9E9E9] text-[#999] px-1 py-0.5 rounded">
            준비 중
          </span>
        )}
      </div>
      <div className="text-[11px] text-[#999] mt-0.5">{desc}</div>
    </div>
    <div className="text-[#CCCCCC]">›</div>
  </button>
);
